import React from 'react';
import { useLocation } from 'react-router-dom';
import ProMarketingLink from './ProMarketingLink';
import { useTranslation } from 'react-i18next';
import { Instagram } from 'lucide-react';
import Logo from '../Logo/Logo';
import { CONFIG_OFFER_LINK_LABEL } from '../../config/configurationOffer';
import { HELP_CENTER_URL } from '../../config/helpCenter';
import {
  FR_MARKETING_METIER_NAV_LINKS,
  localizedHomeAnchorPath,
  localizedMetierNavPath,
} from '../../config/marketingMetierNav';
import { proMarketingInternalHref } from '../SEO/marketingCanonicalUrl';
import './MarketingFooter.css';

export default function MarketingFooter() {
  const { pathname } = useLocation();
  const { t } = useTranslation('marketing');
  // #1120 v2 : pages tarifs/comparatifs FR uniquement — pas de lien FR depuis /en/, /es/…
  const isFrMarketing = !/^\/(en|es|de|it|nl)(\/|$)/.test(pathname);

  return (
    <footer className="mk-footer">
      <div className="mk-footer__inner">
        <div className="mk-footer__grid">
          <div className="mk-footer__brand-col">
            <div className="mk-footer__brand">
              <Logo fontSize="24px" contrast={true} />
            </div>
            <p className="mk-footer__tagline">
              {t('marketingFooter.tagline')}
            </p>
            <div className="mk-footer__social">
              <a
                href="https://www.instagram.com/rintintin.pro"
                target="_blank"
                rel="noopener noreferrer"
                className="mk-footer__social-link"
                aria-label={t('marketingFooter.instagramAriaLabel')}
              >
                <Instagram size={20} />
              </a>
            </div>
          </div>

          <div className="mk-footer__col">
            <div className="mk-footer__col-title">{t('marketingFooter.columns.product')}</div>
            <ul className="mk-footer__links">
              <li><ProMarketingLink to="/fonctionnalites">{t('marketingFooter.links.features')}</ProMarketingLink></li>
              <li><a href={proMarketingInternalHref(localizedHomeAnchorPath('tarifs', pathname))}>{t('marketingFooter.links.pricing')}</a></li>
              <li><ProMarketingLink to="/offre-configuration">{t('marketingFooter.links.configOffer', { defaultValue: CONFIG_OFFER_LINK_LABEL })}</ProMarketingLink></li>
              <li><ProMarketingLink to="/modele-facture-prestation-chien">{t('marketingFooter.links.invoiceTemplate')}</ProMarketingLink></li>
              <li><ProMarketingLink to="/modele-contrat-garde-chien">{t('marketingFooter.links.careContract')}</ProMarketingLink></li>
              {isFrMarketing && <li><ProMarketingLink to="/tarifs-educateur-canin-logiciel">{t('marketingFooter.links.trainerPricing')}</ProMarketingLink></li>}
              <li><ProMarketingLink to="/migrer-whatsapp-excel-vers-logiciel-pro-chien">{t('marketingFooter.links.migrationPlaybook')}</ProMarketingLink></li>
              <li><ProMarketingLink to="/ecole-club-canin-logiciel">{t('marketingFooter.links.ecoleClub')}</ProMarketingLink></li>
              {isFrMarketing && <li><ProMarketingLink to="/comparatif-logiciel-educateur-canin">{t('marketingFooter.links.softwareComparison')}</ProMarketingLink></li>}
              {isFrMarketing && <li><ProMarketingLink to="/comparatif-logiciel-pet-sitter">{t('marketingFooter.links.softwareComparisonSitter')}</ProMarketingLink></li>}
              {isFrMarketing && <li><ProMarketingLink to="/comparatif-logiciel-pension-canine">{t('marketingFooter.links.softwareComparisonPension')}</ProMarketingLink></li>}
              <li><ProMarketingLink to="/alternatives-logiciel-pro-chien">{t('marketingFooter.links.alternativesHub')}</ProMarketingLink></li>
              <li><ProMarketingLink to="/faq">{t('marketingFooter.links.faqHub')}</ProMarketingLink></li>
              <li><ProMarketingLink to="/avis-rintintin-pro">{t('marketingFooter.links.avisHub')}</ProMarketingLink></li>
              <li>
                <a href={HELP_CENTER_URL} target="_blank" rel="noopener noreferrer">
                  {t('marketingFooter.links.helpCenter')}
                </a>
              </li>
              <li>
                <a
                  href="https://whats-new-rintintin.netlify.app"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('marketingFooter.links.whatsNew')}
                </a>
              </li>
              <li><ProMarketingLink to="/partenaires">{t('marketingFooter.links.partners')}</ProMarketingLink></li>
              <li><ProMarketingLink to="/blog">{t('marketingFooter.links.blog')}</ProMarketingLink></li>
            </ul>
          </div>

          <div className="mk-footer__col">
            <div className="mk-footer__col-title">{t('marketingFooter.columns.features')}</div>
            <ul className="mk-footer__links">
              <li><ProMarketingLink to="/fonctionnalites/planning-reservations">{t('marketingFooter.links.planningReservations')}</ProMarketingLink></li>
              <li><ProMarketingLink to="/fonctionnalites/messagerie">{t('marketingFooter.links.messaging')}</ProMarketingLink></li>
              <li><ProMarketingLink to="/fonctionnalites/paiement-facturation">{t('marketingFooter.links.paymentBilling')}</ProMarketingLink></li>
              <li><ProMarketingLink to="/facturation-electronique">{t('marketingFooter.links.eInvoicing2026', { defaultValue: 'E-facturation 2026 (France)' })}</ProMarketingLink></li>
              <li><ProMarketingLink to="/acaced">{t('marketingFooter.links.acacedOffer', { defaultValue: 'Offre ACACED (obtenu dans les 6 derniers mois)' })}</ProMarketingLink></li>
              <li><ProMarketingLink to="/fonctionnalites/gestion-clients-animaux">{t('marketingFooter.links.clientManagement')}</ProMarketingLink></li>
              <li><ProMarketingLink to="/fonctionnalites/statistiques">{t('marketingFooter.links.statistics')}</ProMarketingLink></li>
              <li><ProMarketingLink to="/fonctionnalites/forfaits-credits">{t('marketingFooter.links.packagesCredits')}</ProMarketingLink></li>
              <li><ProMarketingLink to="/fonctionnalites/cartes-cadeaux">{t('marketingFooter.links.giftCards', { defaultValue: 'Cartes cadeaux' })}</ProMarketingLink></li>
              <li><ProMarketingLink to="/fonctionnalites/emails">{t('marketingFooter.links.proEmails', { defaultValue: 'Emails à ton image' })}</ProMarketingLink></li>
              <li><ProMarketingLink to="/fonctionnalites/groupes-clients">{t('marketingFooter.links.clientGroups', { defaultValue: 'Groupes clients' })}</ProMarketingLink></li>
              <li><ProMarketingLink to="/fonctionnalites/double-sync-google">{t('marketingFooter.links.doubleSyncGoogle', { defaultValue: 'Double Sync Google' })}</ProMarketingLink></li>
              <li><ProMarketingLink to="/fonctionnalites/frais-kilometriques">{t('marketingFooter.links.mileageFees', { defaultValue: 'Frais kilométriques' })}</ProMarketingLink></li>
              <li><ProMarketingLink to="/fonctionnalites/rappels-sms">{t('marketingFooter.links.smsReminders', { defaultValue: 'Rappels SMS' })}</ProMarketingLink></li>
              <li><ProMarketingLink to="/fonctionnalites/profil-professionnel">{t('marketingFooter.links.professionalProfile', { defaultValue: 'Profil professionnel' })}</ProMarketingLink></li>
              <li><ProMarketingLink to="/fonctionnalites/application-mobile">{t('marketingFooter.links.mobileApp', { defaultValue: 'Application mobile' })}</ProMarketingLink></li>
              <li><ProMarketingLink to="/fonctionnalites/pension-hebergement">{t('marketingFooter.links.boarding', { defaultValue: 'Pension, garderie & hébergement' })}</ProMarketingLink></li>
            </ul>
          </div>

          <div className="mk-footer__col">
            <div className="mk-footer__col-title">{t('marketingFooter.columns.professions')}</div>
            <ul className="mk-footer__links">
              {FR_MARKETING_METIER_NAV_LINKS.map((item) => (
                <li key={item.path}>
                  <ProMarketingLink to={localizedMetierNavPath(item.path, pathname)}>{t(item.labelKey)}</ProMarketingLink>
                </li>
              ))}
              <li><ProMarketingLink to="/be">{t('marketingFooter.links.belgium', { defaultValue: 'Belgique' })}</ProMarketingLink></li>
              <li>
                <ProMarketingLink to="/be/nl">
                  {t('marketingFooter.links.flanders', { defaultValue: 'Flandre / Nederlands' })}
                </ProMarketingLink>
              </li>
              <li>
                <ProMarketingLink to="/nl">
                  {t('marketingFooter.links.netherlands', { defaultValue: 'Pays-Bas / Nederland' })}
                </ProMarketingLink>
              </li>
            </ul>
          </div>

          <div className="mk-footer__col">
            <div className="mk-footer__col-title">{t('marketingFooter.columns.legal')}</div>
            <ul className="mk-footer__links">
              <li><ProMarketingLink to="/terms-of-use">{t('marketingFooter.links.termsOfUse')}</ProMarketingLink></li>
              <li><ProMarketingLink to="/privacy-policy">{t('marketingFooter.links.privacyPolicy')}</ProMarketingLink></li>
              <li><ProMarketingLink to="/cgv">{t('marketingFooter.links.termsOfSale')}</ProMarketingLink></li>
              <li><ProMarketingLink to="/legal">{t('marketingFooter.links.legalNotice')}</ProMarketingLink></li>
            </ul>
          </div>
        </div>

        <div className="mk-footer__bottom">
          <p>&copy; {new Date().getFullYear()} Rintintin. {t('marketingFooter.copyright')}</p>
        </div>
      </div>
    </footer>
  );
}
